import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import chatService from '../services/chatService';
import authService from '../services/authService';

/**
 * Página para exibir e interagir com uma conversa específica do chatbot
 */
const ChatPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversation();
  }, [sessionId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversation = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Verificar autenticação
      if (!authService.isAuthenticated()) {
        navigate('/login');
        return;
      }

      console.log('🔍 Carregando conversa:', sessionId);
      
      // Carregar mensagens da sessão
      const sessionMessages = await chatService.getSessionMessages(sessionId);
      console.log('📜 Mensagens carregadas:', sessionMessages);
      
      if (sessionMessages && sessionMessages.data) {
        setMessages(sessionMessages.data.messages || sessionMessages.data || []);
      } else {
        setMessages([]);
      }
      
      // Encontrar dados da sessão nas sessões do usuário
      const allSessions = await chatService.getSessions({ limit: 100 });
      const currentSession = allSessions.find(s => s.sessionId === sessionId);
      
      if (currentSession) {
        const formattedSession = chatService.formatSessionForDisplay(currentSession);
        setSession(formattedSession);
        console.log('✅ Sessão encontrada:', formattedSession);
      } else {
        console.warn('⚠️ Sessão não encontrada na lista de sessões do usuário');
        // Criar sessão básica com ID
        setSession({
          id: sessionId,
          title: `Conversa ${new Date().toLocaleDateString()}`,
          family: 'Individual',
          type: 'Pessoal',
          status: 'Ativa'
        });
      }
      
    } catch (error) {
      console.error('❌ Erro ao carregar conversa:', error);
      setError('Erro ao carregar a conversa. Verifique se você tem acesso a esta sessão.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || sending) return;

    try {
      setSending(true);
      const message = inputMessage.trim();
      setInputMessage('');

      // Adicionar mensagem do usuário à interface imediatamente
      const userMessage = {
        type: 'user',
        content: message,
        timestamp: new Date().toISOString(),
        sender: {
          name: 'Você'
        }
      };
      setMessages(prev => [...prev, userMessage]);

      console.log('💬 Enviando mensagem:', message, 'para sessão:', sessionId);
      
      // Enviar mensagem para o chatbot
      const response = await chatService.sendMessage(sessionId, message);
      console.log('✅ Resposta recebida:', response);
      
      // Adicionar resposta do bot
      if (response && response.data) {
        const botMessage = {
          type: 'bot',
          content: response.data.data?.message || response.data.message || response.data.response || 'Resposta não disponível.',
          timestamp: new Date().toISOString(),
          sender: {
            name: 'Assistente'
          }
        };
        setMessages(prev => [...prev, botMessage]);
      }
      
    } catch (error) {
      console.error('❌ Erro ao enviar mensagem:', error);
      
      // Adicionar mensagem de erro
      const errorMessage = {
        type: 'system',
        content: 'Erro ao enviar mensagem. Tente novamente.',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch (error) {
      return '';
    }
  };

  const renderMessage = (message, index) => {
    const isUser = message.type === 'user';
    const isSystem = message.type === 'system';
    const isError = message.isError;

    return (
      <div
        key={index}
        className={`flex mb-4 ${isUser ? 'justify-end' : 'justify-start'}`}
      >
        <div
          className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
            isError
              ? 'bg-red-100 text-red-800 border border-red-200'
              : isSystem
              ? 'bg-gray-100 text-gray-600 text-center w-full'
              : isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground'
          }`}
        >
          <div className="text-sm whitespace-pre-wrap">
            {message.content}
          </div>
          <div className={`text-xs mt-1 opacity-70 ${isSystem ? 'hidden' : ''}`}>
            {formatMessageTime(message.timestamp)}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <header className="border-b bg-white/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-primary rounded-full"></div>
              <h1 className="text-xl font-bold">Casal Resolve</h1>
            </div>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              Voltar ao Dashboard
            </Button>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8 text-center">
              <div className="text-red-500 text-6xl mb-4">❌</div>
              <h2 className="text-2xl font-bold mb-2">Erro ao Carregar Conversa</h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => navigate('/dashboard')}>
                  Voltar ao Dashboard
                </Button>
                <Button variant="outline" onClick={loadConversation}>
                  Tentar Novamente
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5">
      <header className="border-b bg-white/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-primary rounded-full"></div>
            <h1 className="text-xl font-bold">Casal Resolve</h1>
          </div>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Voltar ao Dashboard
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header da Conversa */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-secondary rounded-full"></div>
                  {session?.title || 'Conversa'}
                </CardTitle>
                <p className="text-muted-foreground mt-1">
                  {session?.family} • {session?.type} • {session?.status}
                </p>
              </div>
              <div className="text-sm text-muted-foreground">
                {messages.length} mensagens
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Área de Mensagens */}
        <Card className="mb-6">
          <CardContent className="p-0">
            <div className="h-96 overflow-y-auto p-4 border-b">
              {messages.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <div className="text-4xl mb-2">💬</div>
                  <p>Nenhuma mensagem ainda nesta conversa.</p>
                  <p className="text-sm">Comece digitando uma mensagem abaixo!</p>
                </div>
              ) : (
                messages.map((message, index) => renderMessage(message, index))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input de Mensagem */}
            <div className="p-4">
              <div className="flex gap-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Digite sua mensagem..."
                  disabled={sending}
                  className="flex-1"
                />
                <Button 
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || sending}
                >
                  {sending ? '⏳' : '📤'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Pressione Enter para enviar, Shift+Enter para nova linha
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Informações da Sessão */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Informações da Sessão</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">ID da Sessão:</span>
                <p className="text-muted-foreground font-mono text-xs">{sessionId}</p>
              </div>
              <div>
                <span className="font-medium">Status:</span>
                <p className="text-muted-foreground">{session?.status || 'Ativa'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ChatPage;